import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MoreVertOutlined from "@mui/icons-material/MoreVertOutlined";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Select,
  Slide,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import axios from "axios";
import React, { useEffect, useState, useCallback, forwardRef } from "react";
import { Company, Employee, LandingPage, WebArtifacts } from "../../types";
import { User, hostname, parseError, formatRelativeTime, isSafeUrl } from "../../utils";
import LoadingButton from "../../components/LoadingButton";
import LandingPageBuilder from "./LandingPageBuilder";

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface LandingPagesListProps {
  currentUser: User;
  company: Company;
  companyId: string;
  employees: Employee[];
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  currentPlan: string;
}

const LandingPagesList: React.FC<LandingPagesListProps> = ({
  currentUser,
  company,
  companyId,
  employees,
  getToken,
  eventListener,
  currentPlan,
}) => {
  const [landingPages, setLandingPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [builderDialogOpen, setBuilderDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<LandingPage | null>(null);
  const [newPageName, setNewPageName] = useState("");
  const [newPageDescription, setNewPageDescription] = useState("");
  const [selectedArtifactsId, setSelectedArtifactsId] = useState<string>("");
  const [selectedListId, setSelectedListId] = useState<string>("");
  const [artifactsList, setArtifactsList] = useState<WebArtifacts[]>([]);
  const [smartLists, setSmartLists] = useState<{ id: string; name: string }[]>([]);
  const [loadingArtifacts, setLoadingArtifacts] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [creatingList, setCreatingList] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [publishing, setPublishing] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPage, setMenuPage] = useState<LandingPage | null>(null);

  const fetchLandingPages = useCallback(async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/landing-pages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId },
      });
      setLandingPages(response.data.items || []);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setLoading(false);
    }
  }, [companyId, getToken, eventListener]);

  useEffect(() => {
    fetchLandingPages();
  }, [fetchLandingPages]);

  const fetchCreateDialogData = useCallback(async () => {
    setLoadingArtifacts(true);
    setLoadingLists(true);
    try {
      const token = await getToken();

      // Fetch artifacts and lists in parallel
      const [artifactsResponse, listsResponse] = await Promise.all([
        axios.get(`${hostname}/web-artifacts`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId, listAll: true },
        }),
        axios.get(`${hostname}/lists`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyId, page: 1, pageSize: 100, minimal: true },
        }),
      ]);

      setArtifactsList(artifactsResponse.data.items || []);
      const listItems = listsResponse.data.items || [];
      setSmartLists(listItems.map((list: { id: string; name: string }) => ({
        id: list.id,
        name: list.name,
      })));
    } catch (error) {
      // Non-critical, don't show error
      console.error("Failed to fetch create dialog data:", error);
    } finally {
      setLoadingArtifacts(false);
      setLoadingLists(false);
    }
  }, [companyId, getToken]);

  // Fetch artifacts and lists when create dialog opens
  useEffect(() => {
    if (createDialogOpen) {
      fetchCreateDialogData();
    }
  }, [createDialogOpen, fetchCreateDialogData]);

  const handleCreateSmartList = async () => {
    if (!newPageName.trim()) return;

    setCreatingList(true);
    try {
      const token = await getToken();
      const listName = `${newPageName.trim()} Leads`;

      const response = await axios.post(
        `${hostname}/lists`,
        {
          companyId,
          listDict: { name: listName, type: 'leads' },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newList = response.data;

      // Add to local list and select it
      setSmartLists((prev) => [...prev, { id: newList.id, name: newList.name }]);
      setSelectedListId(newList.id);

      eventListener({ type: "SET_SUCCESS", message: `Created smart list "${listName}"` });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setCreatingList(false);
    }
  };

  const handleCreatePage = async () => {
    if (!newPageName.trim()) return;

    setCreating(true);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/landing-pages`,
        {
          companyId,
          name: newPageName.trim(),
          description: newPageDescription.trim() || undefined,
          artifactsId: selectedArtifactsId || undefined,
          listId: selectedListId || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const newPage = response.data;
      setLandingPages((prev) => [newPage, ...prev]);
      setCreateDialogOpen(false);
      setNewPageName("");
      setNewPageDescription("");
      setSelectedArtifactsId("");
      setSelectedListId("");

      // Open the builder dialog
      setSelectedPage(newPage);
      setBuilderDialogOpen(true);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setCreating(false);
    }
  };

  const handleDeletePage = async () => {
    if (!selectedPage) return;

    setDeleting(true);
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/landing-pages/${selectedPage.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyId },
      });

      setLandingPages((prev) => prev.filter((p) => p.id !== selectedPage.id));
      setDeleteDialogOpen(false);
      setSelectedPage(null);
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setDeleting(false);
    }
  };

  const handlePublish = async (page: LandingPage) => {
    setPublishing(page.id);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/landing-pages/${page.id}/publish`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLandingPages((prev) =>
        prev.map((p) => (p.id === page.id ? response.data : p))
      );
      eventListener({ type: "SET_SUCCESS", message: "Landing page published successfully" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setPublishing(null);
    }
  };

  const handleUnpublish = async (page: LandingPage) => {
    setPublishing(page.id);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/landing-pages/${page.id}/unpublish`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLandingPages((prev) =>
        prev.map((p) => (p.id === page.id ? response.data : p))
      );
      eventListener({ type: "SET_SUCCESS", message: "Landing page unpublished" });
    } catch (error) {
      eventListener({ type: "SET_ERROR", error: parseError(error) });
    } finally {
      setPublishing(null);
    }
  };

  const handleOpenBuilder = (page: LandingPage) => {
    setSelectedPage(page);
    setBuilderDialogOpen(true);
  };

  const handleCloseBuilder = () => {
    setBuilderDialogOpen(false);
    setSelectedPage(null);
    // Refresh the list in case changes were made
    fetchLandingPages();
  };

  const getStatusColor = (status: string): "default" | "primary" | "warning" => {
    switch (status) {
      case "published":
        return "primary";
      case "archived":
        return "warning";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Landing Pages</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Landing Page
        </Button>
      </Box>

      {landingPages.length === 0 ? (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary" gutterBottom>
            No landing pages yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your first landing page to get started with AI-assisted web page building.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Your First Landing Page
          </Button>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {landingPages.map((page) => (
            <Grid item xs={12} sm={6} md={4} key={page.id}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                {page.thumbnailUrl ? (
                  <CardMedia
                    component="img"
                    height="140"
                    image={page.thumbnailUrl}
                    alt={page.name}
                    sx={{ objectFit: "cover", flexShrink: 0 }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 140,
                      flexShrink: 0,
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No preview
                    </Typography>
                  </Box>
                )}
                <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="h6" noWrap sx={{ maxWidth: "70%" }}>
                      {page.name}
                    </Typography>
                    <Chip
                      label={page.status.charAt(0).toUpperCase() + page.status.slice(1)}
                      size="small"
                      color={getStatusColor(page.status)}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      minHeight: "2.5em",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {page.description || "\u00A0"}
                  </Typography>
                  {page.isPublished && (page.publicUrl || page.publicSlug) && (
                    <Box sx={{ mt: 1 }}>
                      <Link
                        href={page.publicUrl || `/p/${page.publicSlug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          fontSize: "0.75rem",
                          color: "primary.main",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                          maxWidth: "100%",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {page.publicUrl || `/p/${page.publicSlug}`}
                        </Typography>
                        <OpenInNewIcon sx={{ fontSize: 12, flexShrink: 0 }} />
                      </Link>
                    </Box>
                  )}
                  <Box sx={{ mt: "auto", pt: 1 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {page.created_date ? `Created ${formatRelativeTime(page.created_date)}` : "\u00A0"}
                    </Typography>
                  </Box>
                </CardContent>
                <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 1, flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setMenuAnchorEl(e.currentTarget);
                      setMenuPage(page);
                    }}
                  >
                    {publishing === page.id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <MoreVertOutlined />
                    )}
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuPage(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuPage) handleOpenBuilder(menuPage);
            setMenuAnchorEl(null);
            setMenuPage(null);
          }}
        >
          Edit
        </MenuItem>
        {menuPage?.isPublished ? (
          [
            <MenuItem
              key="view"
              onClick={() => {
                const pageUrl = menuPage.publicUrl || `/p/${menuPage.publicSlug}`;
                const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
                if (safeUrlPattern.test(pageUrl)) window.open(pageUrl, "_blank");
                setMenuAnchorEl(null);
                setMenuPage(null);
              }}
            >
              View Published Page
            </MenuItem>,
            <MenuItem
              key="unpublish"
              onClick={() => {
                if (menuPage) handleUnpublish(menuPage);
                setMenuAnchorEl(null);
                setMenuPage(null);
              }}
              disabled={publishing === menuPage?.id}
            >
              Unpublish
            </MenuItem>,
          ]
        ) : (
          <MenuItem
            onClick={() => {
              if (menuPage) handlePublish(menuPage);
              setMenuAnchorEl(null);
              setMenuPage(null);
            }}
            disabled={publishing === menuPage?.id}
          >
            Publish
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setSelectedPage(menuPage);
            setDeleteDialogOpen(true);
            setMenuAnchorEl(null);
            setMenuPage(null);
          }}
          sx={{ color: "error.main" }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Landing Page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Page Name"
            value={newPageName}
            onChange={(e) => setNewPageName(e.target.value)}
            sx={{ mt: 1 }}
            placeholder="e.g., Product Launch Campaign"
          />
          <TextField
            fullWidth
            label="Description (Optional)"
            value={newPageDescription}
            onChange={(e) => setNewPageDescription(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            rows={2}
            placeholder="Brief description of this landing page"
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="artifacts-select-label">Brand Kit (Optional)</InputLabel>
            <Select
              labelId="artifacts-select-label"
              value={selectedArtifactsId}
              label="Brand Kit (Optional)"
              onChange={(e) => setSelectedArtifactsId(e.target.value)}
              disabled={loadingArtifacts}
            >
              <MenuItem value="">
                <em>None - Create new artifacts later</em>
              </MenuItem>
              {artifactsList.map((artifact) => (
                <MenuItem key={artifact.id} value={artifact.id}>
                  {artifact.name || "Unnamed"}{artifact.isDefault ? " (Default)" : ""}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Link existing brand assets (favicon, logo, images) to this landing page
            </FormHelperText>
          </FormControl>
          <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "flex-start" }}>
            <FormControl fullWidth>
              <InputLabel id="list-select-label">Smart List for Form Capture (Optional)</InputLabel>
              <Select
                labelId="list-select-label"
                value={selectedListId}
                label="Smart List for Form Capture (Optional)"
                onChange={(e) => setSelectedListId(e.target.value)}
                disabled={loadingLists}
              >
                <MenuItem value="">
                  <em>None - Add a list later</em>
                </MenuItem>
                {smartLists.map((list) => (
                  <MenuItem key={list.id} value={list.id}>
                    {list.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Attach a smart list to capture form submissions from this landing page
              </FormHelperText>
            </FormControl>
            <Tooltip title={newPageName.trim() ? `Create new list: "${newPageName.trim()} Leads"` : "Enter a page name first"}>
              <span>
                <LoadingButton
                  variant="outlined"
                  onClick={handleCreateSmartList}
                  loading={creatingList}
                  disabled={loadingLists || !newPageName.trim()}
                  sx={{ minWidth: 48, height: 56, px: 1 }}
                >
                  <AddIcon />
                </LoadingButton>
              </span>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={handleCreatePage}
            loading={creating}
            disabled={!newPageName.trim()}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Landing Page</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Are you sure you want to delete "{selectedPage?.name}"? This action cannot be undone.
            {selectedPage?.isPublished && (
              <Box sx={{ mt: 1, color: "warning.main" }}>
                This page is currently published and will be removed from the web.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={handleDeletePage}
            loading={deleting}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Builder Fullscreen Dialog */}
      <Dialog
        fullScreen
        open={builderDialogOpen}
        onClose={handleCloseBuilder}
        TransitionComponent={Transition}
      >
        {selectedPage && (
          <LandingPageBuilder
            currentUser={currentUser}
            companyId={companyId}
            company={company}
            employees={employees}
            getToken={getToken}
            eventListener={eventListener}
            currentPlan={currentPlan}
            pageId={selectedPage.id}
            pageName={selectedPage.name}
            onClose={handleCloseBuilder}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default LandingPagesList;
